import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

// Animations
export const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Form Container
export const FormContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 30px;
  background: var(--white, #ffffff);
  border-radius: var(--rounded-xl, 1.5rem);
  box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  border: 1px solid rgba(6, 182, 212, 0.2);
  position: relative;
  overflow: hidden;
  z-index: 100;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(135deg, var(--primary-blue, #1e3a8a), var(--secondary-cyan, #06b6d4));
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    max-width: 100%;
  }
`;

// Form Elements
export const FormTitle = styled.h3`
  font-size: 1.8rem;
  color: var(--text-dark, #1f2937);
  margin-bottom: 20px;
  text-align: center;
  font-weight: 600;
  
  span {
    color: var(--secondary-cyan, #06b6d4);
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--neutral-gray, #6b7280);
  font-size: 0.9rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid var(--light-gray, #f3f4f6);
  border-radius: var(--rounded, 1rem);
  font-size: 1rem;
  transition: all 0.3s ease;
  background: var(--white, #ffffff);
  
  &:focus {
    outline: none;
    border-color: var(--secondary-cyan, #06b6d4);
    box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.2);
  }
  
  &::placeholder {
    color: var(--neutral-gray, #6b7280);
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid var(--light-gray, #f3f4f6);
  border-radius: var(--rounded, 1rem);
  font-size: 1rem;
  transition: all 0.3s ease;
  background: var(--white, #ffffff);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2306b6d4' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 15px center;
  
  &:focus {
    outline: none;
    border-color: var(--secondary-cyan, #06b6d4);
    box-shadow: 0 0 0 2px rgba(6, 182, 212, 0.2);
  }
`;

export const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, var(--primary-blue, #1e3a8a), var(--secondary-cyan, #06b6d4));
  color: var(--white, #ffffff);
  border: none;
  border-radius: var(--rounded, 1rem);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  box-shadow: var(--shadow, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: var(--neutral-gray, #6b7280);
    cursor: not-allowed;
    transform: none;
  }
`;

export const FormRow = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

export const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.8rem;
  margin-top: 5px;
  font-weight: 500;
`;

export const SuccessMessage = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  padding: 15px;
  border-radius: var(--rounded, 1rem);
  margin-bottom: 20px;
  color: var(--success-green, #10b981);
  text-align: center;
  font-weight: 500;
`;

export const PhoneInputWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const PhonePrefix = styled.div`
  background: rgba(6, 182, 212, 0.1);
  padding: 12px 15px;
  border-radius: var(--rounded, 1rem) 0 0 var(--rounded, 1rem);
  border: 1px solid var(--light-gray, #f3f4f6);
  border-right: none;
  color: var(--neutral-gray, #6b7280);
  font-weight: 500;
`;

// Steps
export const PaymentSteps = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 15px;
    left: 10%;
    right: 10%;
    height: 2px;
    background-color: rgba(6, 182, 212, 0.3);
    z-index: 0;
  }
`;

export const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  
  .step-number {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: ${props => props.active ? 'var(--secondary-cyan, #06b6d4)' : props.completed ? 'var(--success-green, #10b981)' : 'var(--light-gray, #f3f4f6)'};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    margin-bottom: 8px;
    transition: all 0.3s ease;
  }
  
  .step-label {
    font-size: 0.8rem;
    color: ${props => props.active ? 'var(--text-dark, #1f2937)' : props.completed ? 'var(--success-green, #10b981)' : 'var(--neutral-gray, #6b7280)'};
    font-weight: ${props => props.active || props.completed ? '600' : '400'};
  }
`;

// Confirmation elements
export const ConfirmationMessage = styled.div`
  text-align: center;
  padding: 20px;
  
  svg {
    font-size: 3rem;
    color: var(--success-green, #10b981);
    margin-bottom: 15px;
  }
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 10px;
    color: var(--text-dark, #1f2937);
  }
  
  p {
    color: var(--neutral-gray, #6b7280);
    margin-bottom: 20px;
    line-height: 1.6;
  }
  
  .confirmation-number {
    font-family: monospace;
    background-color: rgba(6, 182, 212, 0.1);
    padding: 10px 15px;
    border-radius: var(--rounded, 1rem);
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--secondary-cyan, #06b6d4);
    margin: 15px 0;
    display: inline-block;
  }
`;

// Payment elements
export const PaymentContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  border: 1px solid rgba(6, 182, 212, 0.3);
  border-radius: var(--rounded, 1rem);
  background-color: rgba(6, 182, 212, 0.05);
`;

export const PaymentTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 15px;
  color: var(--text-dark, #1f2937);
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const PriceTag = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-dark, #1f2937);
  margin: 15px 0;
  
  .currency {
    color: var(--secondary-cyan, #06b6d4);
  }
`;

export const PaymentButton = styled(Button)`
  box-shadow: var(--shadow, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
  margin-top: 15px;
  
  &:hover {
    box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  }
`;

export const PriceList = styled.div`
  margin: 15px 0;
`;

export const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.1);
  
  &:last-child {
    border-bottom: none;
    font-weight: bold;
    margin-top: 5px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

export const EarlyBirdBadge = styled.span`
  background-color: var(--success-green, #10b981);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;
